export const KEYWORD_MAPPINGS_STORE = 'keywordMappings'

export const INVALIDATE_KEYWORD_MAPPINGS = `INVALIDATE_${KEYWORD_MAPPINGS_STORE.toUpperCase()}`

const IGNORED_ERROR_KEYS = new Set(['status', 'error', 'key'])

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null
}

function readRuleMessage(value: unknown): string | undefined {
    if (typeof value === 'string') {
        if (value === '') return undefined
        return value
    }
    if (!isRecord(value)) return undefined

    const messages = Object.values(value).filter(
        (message): message is string => typeof message === 'string' && message !== '',
    )
    if (messages.length === 0) return undefined

    return messages.join(' ')
}

export function readFieldErrors(response: unknown): Record<string, string> {
    if (!isRecord(response) || response.error !== 'fields') return {}

    const errors: Record<string, string> = {}
    for (const [field, value] of Object.entries(response)) {
        if (IGNORED_ERROR_KEYS.has(field)) continue
        const message = readRuleMessage(value)
        if (message === undefined) continue
        errors[field] = message
    }

    return errors
}

export function readGenericError(response: unknown, fallback: string): string {
    if (!isRecord(response)) return fallback
    if (typeof response.error !== 'string') return fallback
    if (response.error === '' || response.error === 'fields') return fallback

    return response.error
}

export function readServiceId(value: unknown): number | null {
    if (typeof value === 'number' && Number.isInteger(value)) return value
    if (typeof value === 'string' && value !== '' && Number.isInteger(Number(value))) {
        return Number(value)
    }

    return null
}

export function readServiceIds(value: unknown): number[] {
    if (!Array.isArray(value)) return []

    return value.flatMap((id) => {
        const parsed = readServiceId(id)
        if (parsed === null) return []
        return [parsed]
    })
}

export function withoutField(
    errors: Record<string, string>,
    field: string,
): Record<string, string> {
    if (errors[field] === undefined) return errors

    const next = { ...errors }
    delete next[field]
    return next
}

export function createTempKeywordMappingId(): string {
    const random = Math.random().toString(36).slice(2, 8)
    return `tmp${Date.now()}${random}`
}

export const KEYWORD_MAPPING_SEARCH_INTENTS_STORE = 'keywordMappingSearchIntents'

export const INVALIDATE_KEYWORD_MAPPING_SEARCH_INTENTS = `INVALIDATE_${KEYWORD_MAPPING_SEARCH_INTENTS_STORE.toUpperCase()}`

export const KEYWORD_MAPPING_FOCUS_RING =
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5a8bfc]'

export function readStringList(value: unknown): string[] {
    if (!Array.isArray(value)) return []
    return value.filter((item): item is string => typeof item === 'string')
}

export function readPlaceholderNames(extra: unknown): string[] {
    if (!isRecord(extra)) return []
    return readStringList(extra.h1Placeholders)
}

export function h1HelpText(placeholders: string[]): string {
    const italic = 'Text wrapped in asterisks, such as *text*, renders in italic.'
    if (placeholders.length === 0) return italic

    const listed = placeholders.map((name) => `{{${name}}}`).join(', ')
    return `Placeholders: ${listed}. ${italic}`
}
